/**
 * Lock screen and headset controls, via the Media Session API.
 *
 * This is the single most requested audiobook feature: without it, a listener
 * has to unlock the phone to pause or skip while walking. The API is still
 * inconsistently implemented (Safari throws on action names it does not
 * recognize instead of ignoring them), so every registration is wrapped in its
 * own try/catch and the whole module is a no-op where "mediaSession" is absent
 * from navigator.
 */

export interface MediaSessionBook {
  title: string;
  author?: string;
  cover?: string;
}

export interface MediaSessionHandlers {
  play(): void;
  pause(): void;
  /** Jump back/forward by a fixed number of seconds. Never a chapter change. */
  seekBack(): void;
  seekForward(): void;
  seekTo(time: number): void;
  previousChapter?(): void;
  nextChapter?(): void;
}

export interface BindMediaSessionOptions {
  /**
   * The lock screen scrubber calls "seekto" continuously while a finger drags
   * it, including once on attach with a stale time on some platforms (notably
   * when the screen is touched from inside a pocket). Wiring it by default has
   * cost real listeners their position. Callers that have verified their
   * seekTo handler is safe against spurious calls can opt in explicitly.
   */
  enableSeekTo?: boolean;
}

type MediaSessionActionName =
  | "play"
  | "pause"
  | "seekbackward"
  | "seekforward"
  | "seekto"
  | "previoustrack"
  | "nexttrack";

const ALL_ACTIONS: MediaSessionActionName[] = [
  "play",
  "pause",
  "seekbackward",
  "seekforward",
  "seekto",
  "previoustrack",
  "nexttrack",
];

function hasMediaSession(): boolean {
  return typeof navigator !== "undefined" && "mediaSession" in navigator;
}

/** Safari (and older WebKit-based WebViews) throw on action names they do not implement. */
function setHandler(action: MediaSessionActionName, handler: MediaSessionActionHandler | null): void {
  try {
    navigator.mediaSession.setActionHandler(action, handler);
  } catch {
    // Unsupported action on this platform: nothing to fall back to.
  }
}

function artworkFor(cover: string): MediaImage[] {
  // We do not have differently-sized renditions of the cover, only the one
  // URL. Declaring several sizes still helps: the OS picks the closest match
  // instead of upscaling a single declared size, and some platforms refuse to
  // show artwork at all when only one size is offered.
  return [96, 256, 512].map((size) => ({
    src: cover,
    sizes: `${size}x${size}`,
    type: "image/jpeg",
  }));
}

/**
 * Register lock screen / headset handlers for the book currently playing.
 * Returns a detach function that clears every handler and the metadata, so
 * the caller can call it unconditionally before binding the next book.
 */
export function bindMediaSession(
  book: MediaSessionBook,
  handlers: MediaSessionHandlers,
  options?: BindMediaSessionOptions,
): () => void {
  if (!hasMediaSession()) return () => {};

  try {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: book.title,
      artist: book.author ?? "",
      artwork: book.cover ? artworkFor(book.cover) : [],
    });
  } catch {
    // MediaMetadata itself can throw on malformed input; the controls still
    // work without metadata, just unlabeled.
  }

  setHandler("play", () => handlers.play());
  setHandler("pause", () => handlers.pause());
  setHandler("seekbackward", () => handlers.seekBack());
  setHandler("seekforward", () => handlers.seekForward());

  if (options?.enableSeekTo) {
    setHandler("seekto", (details) => {
      if (typeof details.seekTime === "number") handlers.seekTo(details.seekTime);
    });
  }

  if (handlers.previousChapter) {
    setHandler("previoustrack", () => handlers.previousChapter?.());
  }
  if (handlers.nextChapter) {
    setHandler("nexttrack", () => handlers.nextChapter?.());
  }

  return () => {
    if (!hasMediaSession()) return;
    for (const action of ALL_ACTIONS) setHandler(action, null);
    try {
      navigator.mediaSession.metadata = null;
    } catch {
      // Nothing to clean up if this throws.
    }
  };
}

/**
 * Tell the OS where we are in the track, so the lock screen scrubber and
 * elapsed/remaining time are correct. Chrome throws a TypeError if duration is
 * not a finite positive number or if position exceeds duration, which would
 * otherwise take playback down with it, so both are defended here.
 */
export function updateMediaPosition(duration: number, position: number, rate: number): void {
  if (!hasMediaSession()) return;
  if (typeof navigator.mediaSession.setPositionState !== "function") return;
  if (!Number.isFinite(duration) || duration <= 0) return;

  const clampedPosition = Math.min(Math.max(position, 0), duration);

  try {
    navigator.mediaSession.setPositionState({
      duration,
      position: clampedPosition,
      playbackRate: rate,
    });
  } catch {
    // Out-of-range values slipping through the guards above should not break playback.
  }
}

/** Reflects play/pause so the lock screen icon matches the actual state. */
export function setMediaPlaybackState(playing: boolean): void {
  if (!hasMediaSession()) return;
  try {
    navigator.mediaSession.playbackState = playing ? "playing" : "paused";
  } catch {
    // Best effort: the icon may lag, but nothing else depends on this value.
  }
}

/**
 * How far to rewind on resume, as a function of how long playback was paused.
 *
 * Rationale: a pause under 10 s is a reflex (accidental tap, a word missed
 * mid-sentence) and rewinding would just replay what was already heard. Past
 * that, comprehension drops with how long attention was away, so the rewind
 * grows with the pause. It grows at 1 second of rewind per 6 seconds of pause
 * beyond the 10 s threshold, a plain linear ramp with no tuning knobs, and it
 * saturates at 30 s (reached once the pause passes about three minutes) so a
 * multi-hour pause does not throw the listener back an unreasonable distance.
 */
export function rewindOnResume(pausedMs: number): number {
  const pausedSeconds = pausedMs / 1000;
  const NO_REWIND_BELOW_SECONDS = 10;
  const SECONDS_OF_PAUSE_PER_SECOND_OF_REWIND = 6;
  const MAX_REWIND_SECONDS = 30;

  if (pausedSeconds <= NO_REWIND_BELOW_SECONDS) return 0;

  const excess = pausedSeconds - NO_REWIND_BELOW_SECONDS;
  const rewind = excess / SECONDS_OF_PAUSE_PER_SECOND_OF_REWIND;

  return Math.round(Math.min(MAX_REWIND_SECONDS, rewind));
}
